import { Placeholder } from '@tiptap/extension-placeholder';
import { EditorContent, useEditor, useEditorState } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { Bold, Italic, List, ListOrdered, Redo2, Undo2, Underline } from 'lucide-react';
import type React from 'react';
import { useEffect, useImperativeHandle, useRef, useState } from 'react';

import { FormField } from '~/components/form/FormField';
import { Textarea } from '~/components/form/Textarea';
import { Button } from '~/components/ui/button';
import { richTextKeeps } from '~/lib/richText';

export interface RichTextFieldProps {
  id: string;
  label: string;
  /** The stored HTML. Seeds the editor, and replaces its content when it changes from outside. */
  value: string;
  testId: string;
  /** Fires on the operator's edits only, never on load or on an external `value` change. */
  onChange: (html: string) => void;
  helperText?: React.ReactNode;
  /** Replaces helperText. */
  error?: string;
  disabled?: boolean;
  /** Inserts text at the caret (a field picker). */
  insertRef?: React.Ref<{ insertText: (text: string) => void }>;
}

/**
 * A controlled rich text field on TipTap, storing HTML. The editor re-serialises HTML it loads, so
 * `onChange` is silent until the operator edits: untouched HTML is never handed back to the form.
 * HTML the editor would lose markup from (images, tables, styles) is edited as source instead.
 */
export function RichTextField({
  id,
  label,
  value,
  testId,
  onChange,
  helperText,
  error,
  disabled,
  insertRef,
}: RichTextFieldProps) {
  // The last HTML this field reported, so the echo of an edit is not mistaken for an outside change.
  const reported = useRef(value);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const [source, setSource] = useState(() => !richTextKeeps(value));

  const editor = useEditor({
    content: value,
    editable: !disabled,
    extensions: [StarterKit, Placeholder.configure({ placeholder: 'Write here…' })],
    editorProps: {
      attributes: {
        id,
        role: 'textbox',
        'aria-multiline': 'true',
        'aria-label': label,
        'data-testid': testId,
      },
    },
    onUpdate: ({ editor: current, transaction }) => {
      // `setEditable` and other non-edits also emit `update`; only a document change is the operator's edit.
      if (!transaction.docChanged) return;
      const html = current.isEmpty ? '' : current.getHTML();
      reported.current = html;
      onChangeRef.current(html);
    },
  });

  useEffect(() => {
    if (!editor || value === reported.current) return;
    reported.current = value;
    setSource(!richTextKeeps(value));
    editor.commands.setContent(value, { emitUpdate: false });
  }, [editor, value]);

  useEffect(() => {
    editor?.setEditable(!disabled, false);
  }, [editor, disabled]);

  const describedBy = error ? `${id}-error` : helperText ? `${id}-helper` : undefined;
  useEffect(() => {
    if (!editor) return;
    editor.setOptions({
      editorProps: {
        attributes: {
          id,
          role: 'textbox',
          'aria-multiline': 'true',
          'aria-label': label,
          'data-testid': testId,
          ...(error ? { 'aria-invalid': 'true' } : {}),
          ...(describedBy ? { 'aria-describedby': describedBy } : {}),
        },
      },
    });
  }, [editor, id, label, testId, error, describedBy]);

  useImperativeHandle(
    insertRef,
    () => ({
      insertText: (text: string) => {
        if (source) {
          const next = reported.current + text;
          reported.current = next;
          onChangeRef.current(next);
          return;
        }
        editor?.chain().focus().insertContent({ type: 'text', text }).run();
      },
    }),
    [editor, source],
  );

  const state = useEditorState({
    editor,
    selector: ({ editor: current }) => ({
      bold: current?.isActive('bold') ?? false,
      italic: current?.isActive('italic') ?? false,
      underline: current?.isActive('underline') ?? false,
      bulletList: current?.isActive('bulletList') ?? false,
      orderedList: current?.isActive('orderedList') ?? false,
      canUndo: current?.can().undo() ?? false,
      canRedo: current?.can().redo() ?? false,
    }),
  });

  const tool = (
    name: string,
    icon: React.ReactNode,
    run: () => void,
    active = false,
    off = false,
  ) => (
    <Button
      variant="ghost"
      size="icon"
      ariaLabel={name}
      title={name}
      disabled={!editor || disabled || off}
      onClick={run}
      className={active ? 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-white' : undefined}
      testId={`${testId}-${name.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {icon}
    </Button>
  );

  if (source) {
    return (
      <Textarea
        id={id}
        testId={testId}
        label={`${label} (HTML)`}
        value={value}
        rows={8}
        code
        disabled={disabled}
        error={error}
        onChange={(html) => {
          reported.current = html;
          onChangeRef.current(html);
        }}
        helperText={
          <>
            Shown as HTML because it holds markup the editor would drop, such as images, tables or
            styles. {helperText}
          </>
        }
      />
    );
  }

  return (
    <FormField id={id} label={label} helperText={helperText} error={error}>
      <div
        className={`overflow-hidden rounded-lg border bg-white focus-within:ring-2 dark:bg-gray-900 ${
          error
            ? 'border-error-500 focus-within:ring-error-500/20'
            : 'border-gray-300 focus-within:border-brand-500 focus-within:ring-brand-500/20 dark:border-gray-700'
        } ${disabled ? 'opacity-60' : ''}`}
      >
        <div
          role="toolbar"
          aria-label={`${label} formatting`}
          className="flex flex-wrap items-center gap-0.5 border-b border-gray-200 bg-gray-50 px-2 py-1 dark:border-gray-800 dark:bg-white/[0.03]"
        >
          {tool(
            'Bold',
            <Bold className="size-4" aria-hidden="true" />,
            () => editor?.chain().focus().toggleBold().run(),
            state.bold,
          )}
          {tool(
            'Italic',
            <Italic className="size-4" aria-hidden="true" />,
            () => editor?.chain().focus().toggleItalic().run(),
            state.italic,
          )}
          {tool(
            'Underline',
            <Underline className="size-4" aria-hidden="true" />,
            () => editor?.chain().focus().toggleUnderline().run(),
            state.underline,
          )}
          {tool(
            'Bullet list',
            <List className="size-4" aria-hidden="true" />,
            () => editor?.chain().focus().toggleBulletList().run(),
            state.bulletList,
          )}
          {tool(
            'Ordered list',
            <ListOrdered className="size-4" aria-hidden="true" />,
            () => editor?.chain().focus().toggleOrderedList().run(),
            state.orderedList,
          )}
          {tool(
            'Undo',
            <Undo2 className="size-4" aria-hidden="true" />,
            () => editor?.chain().focus().undo().run(),
            false,
            !state.canUndo,
          )}
          {tool(
            'Redo',
            <Redo2 className="size-4" aria-hidden="true" />,
            () => editor?.chain().focus().redo().run(),
            false,
            !state.canRedo,
          )}
        </div>
        <EditorContent
          editor={editor}
          className="min-h-32 px-3 py-2 text-sm text-gray-800 dark:text-white/90 [&_.ProseMirror]:min-h-28 [&_.ProseMirror]:outline-none [&_ol]:mb-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-2 [&_ul]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_p.is-editor-empty:first-child::before]:pointer-events-none [&_p.is-editor-empty:first-child::before]:float-left [&_p.is-editor-empty:first-child::before]:h-0 [&_p.is-editor-empty:first-child::before]:text-gray-400 [&_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]"
        />
      </div>
    </FormField>
  );
}
